import React, { createContext, useContext, useState } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from './AuthContext';

const PaymentContext = createContext();

export const usePayment = () => useContext(PaymentContext);

export const PaymentProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSession, setPaymentSession] = useState(null);
  const { user } = useAuth();

  /**
   * Create a payment session
   */
  const createPaymentSession = async (paymentData) => {
    setError('');
    setLoading(true);

    try {
      // Add authenticated user id if not already provided
      const dataWithUserId = {
        ...paymentData,
        userId: paymentData.userId || user?.id
      };
      
      // Make API request to create payment session
      // apiClient handles base URL and auth headers automatically
      const response = await apiClient.post('/api/payments/create', dataWithUserId);
      
      const session = response.data;
      
      if (!session || !session.checkout_url) {
        throw new Error('Invalid payment session data received');
      }

      setPaymentSession(session);
      setLoading(false);
      return session;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Error creating payment session';
      setError(errorMessage);
      setLoading(false);
      throw error;
    }
  };

  /**
   * Verify payment status
   */
  const checkPaymentStatus = async (paymentId) => {
    if (!paymentId) {
      throw new Error('Payment ID is required');
    }
    
    // Check local storage cache to avoid repeated API calls
    const cachedResult = localStorage.getItem(`payment_verification_${paymentId}`);
    if (cachedResult) {
      try {
        const parsedResult = JSON.parse(cachedResult);
        const cacheAge = Date.now() - parsedResult.timestamp;
        
        // Use cache if it's less than 1 minute old
        if (cacheAge < 60000) {
          return parsedResult.data;
        }
      } catch (e) {
        // Cache parsing failed, continue with normal verification
      }
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/api/payments/verify/${paymentId}`);
      const paymentData = response.data;
      
      // Store payment status in localStorage for future reference
      if (paymentData && paymentData.status) {
        localStorage.setItem('paymentStatus', paymentData.status);
        
        // If it's manually verified, treat as active for the UI
        if (paymentData.status === 'manually_verified') {
          localStorage.setItem('hasCompletedPayment', 'true');
        }
      }
      
      // Cache the result to avoid repeated API calls
      localStorage.setItem(`payment_verification_${paymentId}`, JSON.stringify({
        timestamp: Date.now(),
        data: paymentData
      }));
      
      return paymentData;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Error verifying payment';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cancel subscription
   */
  const cancelSubscription = async (subscriptionId) => {
    setError('Subscription cancellation is currently disabled');
    throw new Error('Subscription cancellation is temporarily disabled');
  };

  /**
   * Reset payment state
   */
  const resetPaymentState = () => {
    setPaymentSession(null);
    setError(null);
  };

  const value = {
    loading,
    error,
    paymentSession,
    createPaymentSession,
    checkPaymentStatus,
    cancelSubscription,
    resetPaymentState
  };

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>;
}; 